import type {
  AuthResultDto,
  CompleteForcedPasswordChangeRequest,
  SessionStateDto,
} from '@napaicp/schemas'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { apiRequest } from '@/lib/api-client'

export const sessionQueryKey = ['session'] as const

/**
 * The frontend's view of who is signed in. It is advisory only: every
 * authorization decision is made again on the server, so a tampered cache can
 * change what is rendered but never what is permitted.
 */
export function useSession(): UseQueryResult<SessionStateDto> {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: () => apiRequest<SessionStateDto>('/api/auth/session'),
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiRequest<SessionStateDto>('/api/auth/logout', { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey })
    },
  })
}

/**
 * BR-083/API-051/DM-028: completes the forced first-login password change a
 * Temporary Initial Password session is restricted to. The server rotates
 * the session cookie as part of this call (SEC-023), so re-fetching
 * `/api/auth/session` afterward picks up the new, unrestricted session —
 * there is nothing else to do client-side to "unlock" the app.
 */
export function useCompleteForcedPasswordChange() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CompleteForcedPasswordChangeRequest) =>
      apiRequest<AuthResultDto>('/api/auth/password-change/complete', { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey })
    },
  })
}
